import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { versionAPI } from '../services/api';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  AlertCircle,
  BookOpen,
  ArrowLeft,
  Loader2
} from 'lucide-react';

// Importar logo
import logoMafy from '../assets/images/logo-mafy.svg';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  // Obtener versión del frontend desde variables de entorno
  const frontendVersion = process.env.REACT_APP_VERSION || '1.0.0';
  const environment = process.env.REACT_APP_ENVIRONMENT || 'development';

  useEffect(() => {
    loadVersionInfo();
  }, []);

  const loadVersionInfo = async () => {
    try {
      setLoadingVersion(true);
      const response = await versionAPI.getVersion();
      setVersionInfo(response.data);
    } catch (error) {
      console.error('Error cargando versión:', error);
      setVersionInfo({
        version: '1.0.0',
        buildDate: new Date().toISOString(),
        environment: 'Desarrollo'
      });
    } finally {
      setLoadingVersion(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      {/* Header de navegación */}
      <header className="relative z-10">
        <nav className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <img
                src={logoMafy}
                alt="Mafy"
                className="h-10 w-10 transition-transform duration-200 group-hover:scale-110"
              />
              <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                Mafy
              </span>
            </Link>
            <Link
              to="/"
              className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Volver al inicio</span>
            </Link>
          </div>
        </nav>
      </header>

      {/* Contenido principal */}
      <div className="flex items-center justify-center min-h-[calc(100vh-120px)] px-4">
        <div className="w-full max-w-md">
          {/* Card principal */}
          <div className="card p-8 shadow-xl">
            {/* Logo y título */}
            <div className="text-center mb-8">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl shadow-lg">
                  <img
                    src={logoMafy}
                    alt="Mafy"
                    className="h-12 w-12 filter brightness-0 invert"
                  />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Iniciar Sesión
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Accede a tu cuenta de Mafy
              </p>
            </div>

            {/* Formulario */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error message */}
              {error && (
                <div className="alert alert-error">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Email field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Correo Electrónico
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="input-field pl-10"
                    placeholder="tu@empresa.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Password field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    className="input-field pl-10 pr-10"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                className={`w-full btn-primary justify-center py-3 text-lg font-semibold transition-all duration-200 ${loading
                    ? 'opacity-75 cursor-not-allowed'
                    : 'hover-lift shadow-glow'
                  }`}
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Iniciando sesión...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span>Iniciar Sesión</span>
                    <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                  </div>
                )}
              </button>
            </form>

            {/* Enlaces adicionales */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <Link
                to="/api-docs"
                className="flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
              >
                <BookOpen className="w-4 h-4" />
                <span>Ver documentación de API</span>
              </Link>
            </div>

            {/* Footer con versión */}
            <div className="mt-6 text-center">
              {loadingVersion ? (
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Cargando versión...</span>
                </div>
              ) : versionInfo ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  API v{versionInfo.apiVersion || '1.0.0'} •
                  Frontend v{frontendVersion} •
                  {environment}
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Frontend v{frontendVersion} • {environment}
                </p>
              )}
            </div>
          </div>

          {/* Información adicional */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              ¿Primera vez usando Mafy?{' '}
              <Link
                to="/api-docs"
                className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 transition-colors duration-200"
              >
                Consulta la documentación
              </Link>
            </p>
          </div>

          {/* Features destacadas */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-lg bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                <Lock className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                Seguro
              </p>
            </div>

            <div className="text-center p-4 rounded-lg bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                <ArrowRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                Rápido
              </p>
            </div>

            <div className="text-center p-4 rounded-lg bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                <BookOpen className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                Documentado
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;